import { ApolloClient } from '@apollo/client/core';
import { differenceInDays } from 'date-fns';
import { defineStore } from 'pinia';

import { ECommerceData } from '~/helpers';
import type { ExperienceCategory, SearchHotel } from '~/domains/graphql';
import {
  SearchDestinationResultType,
  SearchHotelsDocument,
} from '~/domains/graphql';
import { SearchDestination, SearchOrigin } from '~/domains/search';
import { formatDate, FORMATS } from '~/helpers/date.helper';

export type SearchRoom = {
  adults: number;
  childrenAges: number[];
};

// Extends GraphQL SearchHotelsQueryVariables for booking action needed data
export type SearchHotelsVariables = {
  dates?: {
    dateIn: string;
    dateOut: string;
    nights: number;
  };
  destination?: {
    city?: string;
    country?: string;
    hotel?: string;
    ridcode?: string;
  };
  rooms: SearchRoom[];
};

export type SearchStoreState = {
  currency?: string | null;
  dates: Date[];
  destination: SearchDestination;
  experienceCategories: ExperienceCategory[];
  experienceCategoryId: string | null;
  fetchError: Error | null;
  hotelId: string | null;
  includeFlights: boolean;
  loading: boolean;
  origin: SearchOrigin;
  price?: number | null;
  rooms: SearchRoom[];
  searchHotelsResult: {
    hotels: SearchHotel[];
    alternativeHotels: SearchHotel[];
    variables: SearchHotelsVariables;
  };
  validateRoomInputs: boolean;
};

type Getters = {
  availableExperienceCategories: () => ExperienceCategory[];
  childrenAges(): number[];
  currentSearchVariables(): SearchHotelsVariables;
  dateIn(): string;
  dateOut(): string;
  eligibleAlternativeHotels(): SearchHotel[];
  eligibleHotels(): SearchHotel[];
  formattedSearchQueryVariables(): SearchHotelsVariables;
  isRoomInputsValid(): boolean;
  nbAdults(): number;
  nbGuests(): number;
  nbNights(): number;
  nbRooms(): number;
  room(): (index: number) => SearchRoom | undefined;
  selectedExperienceCategoryTitle(): string | undefined;
  ecommerceEvent(): ECommerceData;
};

type Actions = {
  addRoom(): void;
  removeRoom(): void;
  searchHotels(client: ApolloClient<object>): Promise<void>;
  setCurrency(currency: string | null | undefined): void;
  setExperienceCategories(experienceCategories: ExperienceCategory[]): void;
};

function isRoomInputsValid(rooms: SearchRoom[]) {
  return rooms.every(room => {
    return (
      room.adults >= 1 && room.childrenAges.every(age => age >= 0 && age <= 17)
    );
  });
}

function cleanHotel(hotel: SearchHotel, nbRooms: number) {
  if (nbRooms <= 1) return hotel;

  const hotelCopy = JSON.parse(JSON.stringify(hotel)) as SearchHotel;

  if (hotelCopy.bestOffer) {
    delete hotelCopy.bestOffer.memberPrice;
  }

  return hotelCopy;
}

export const useSearchStore = defineStore<
  'search',
  SearchStoreState,
  Getters,
  Actions
>('search', {
  actions: {
    addRoom() {
      this.rooms.push({
        adults: 1,
        childrenAges: [],
      });
    },
    removeRoom() {
      this.rooms.pop();
    },
    async searchHotels(client) {
      if (!this.isRoomInputsValid) {
        this.loading = false;
        return;
      }

      this.fetchError = null;
      this.loading = true;
      this.searchHotelsResult = {
        hotels: [],
        alternativeHotels: [],
        variables: {
          rooms: [],
        },
      };

      const variables = this.formattedSearchQueryVariables;

      if (!Array.isArray(variables.rooms) || variables.rooms.length <= 0) {
        variables.rooms = [{ adults: 2, childrenAges: [] }];
      }

      try {
        const { data } = await client.query({
          fetchPolicy: 'network-only',
          query: SearchHotelsDocument,
          variables,
        });

        this.searchHotelsResult = {
          hotels: (data.searchHotels?.hotels ?? []) as SearchHotel[],
          alternativeHotels: (data.searchHotels?.alternativeHotels ??
            []) as SearchHotel[],
          variables,
        };

        this.fetchError = null;
        this.loading = false;
      } catch (error) {
        this.fetchError = error as Error;
        this.loading = false;
      }
    },
    setCurrency(currency) {
      this.currency = currency;
    },
    setExperienceCategories(experienceCategories) {
      this.experienceCategories = experienceCategories;
    },
  },
  getters: {
    availableExperienceCategories(): ExperienceCategory[] {
      const sortedExperienceCategories = [...this.experienceCategories].sort(
        (a, b) => a.order - b.order,
      );

      if (!this.searchHotelsResult || !this.searchHotelsResult.hotels.length) {
        return sortedExperienceCategories;
      }

      const eligibleExperienceCategories = [] as string[];

      this.searchHotelsResult.hotels.forEach(hotel => {
        hotel.experienceCategories?.forEach(experienceCategory => {
          if (
            experienceCategory.id &&
            !eligibleExperienceCategories.find(
              categoryId => categoryId === experienceCategory.id,
            )
          ) {
            eligibleExperienceCategories.push(experienceCategory.id);
          }
        });
      });

      const availableExperienceCategories = [] as ExperienceCategory[];

      eligibleExperienceCategories.forEach(id => {
        const experience = sortedExperienceCategories.find(
          experienceCategory => experienceCategory.id === id,
        );

        if (experience) {
          availableExperienceCategories.push(experience);
        }
      });

      return [...availableExperienceCategories].sort(
        (a, b) => a.order - b.order,
      );
    },
    childrenAges(): number[] {
      return this.rooms.reduce(
        (roomChildrenAges, room) => roomChildrenAges.concat(room.childrenAges),
        [] as number[],
      );
    },
    dateIn() {
      if (!this.dates?.[0]) {
        return '';
      }
      return formatDate(this.dates[0], FORMATS.apiFormat);
    },
    dateOut() {
      if (Array.isArray(this.dates) && this.dates.length === 2) {
        return formatDate(this.dates[1], FORMATS.apiFormat);
      }
      return '';
    },
    eligibleAlternativeHotels(): SearchHotel[] {
      if (this.experienceCategoryId === 'all') {
        return this.searchHotelsResult.alternativeHotels;
      }

      return this.searchHotelsResult.alternativeHotels.filter(hotel =>
        hotel.experienceCategories?.find(
          experienceCategory =>
            experienceCategory.id === this.experienceCategoryId,
        ),
      );
    },
    eligibleHotels(): SearchHotel[] {
      if (this.experienceCategoryId === 'all') {
        return this.searchHotelsResult.hotels.map(hotel =>
          cleanHotel(hotel, this.nbRooms),
        );
      }

      return this.searchHotelsResult.hotels
        .filter(hotel =>
          hotel.experienceCategories?.find(
            experienceCategory =>
              experienceCategory.id === this.experienceCategoryId,
          ),
        )
        .map(hotel => cleanHotel(hotel, this.nbRooms));
    },
    formattedSearchQueryVariables(): SearchHotelsVariables {
      const variables: SearchHotelsVariables = {
        rooms: [],
      };

      if (this.rooms) {
        variables.rooms = JSON.parse(JSON.stringify(this.rooms));
      } else {
        variables.rooms = [{ adults: 2, childrenAges: [] }];
      }

      if (this.dateIn) {
        variables.dates = {
          dateIn: this.dateIn,
          dateOut: this.dateOut,
          nights: this.nbNights,
        };
      }

      if (this.destination.type) {
        switch (this.destination.type) {
          case SearchDestinationResultType.City:
            variables.destination = { city: this.destination.text };
            break;
          case SearchDestinationResultType.Country:
            variables.destination = { country: this.destination.text };
            break;
          case SearchDestinationResultType.Hotel:
            variables.destination = this.destination.ridcode
              ? { ridcode: this.destination.ridcode }
              : { hotel: this.destination.text };
            break;
          default:
            break;
        }
      }

      return variables;
    },
    isRoomInputsValid(): boolean {
      return isRoomInputsValid(this.rooms);
    },
    nbAdults(): number {
      return this.rooms.reduce((count, room) => count + room.adults, 0);
    },
    nbGuests(): number {
      return this.rooms.reduce(
        (count, room) => count + room.adults + room.childrenAges.length,
        0,
      );
    },
    nbNights() {
      if (Array.isArray(this.dates) && this.dates.length === 2) {
        const duration = Math.abs(
          differenceInDays(this.dates[0], this.dates[1]),
        );

        return duration ?? 0;
      }

      return 0;
    },
    nbRooms(): number {
      return this.rooms.length;
    },
    currentSearchVariables(): SearchHotelsVariables {
      return this.searchHotelsResult?.variables ?? {};
    },
    room(): (index: number) => SearchRoom | undefined {
      return index => this.rooms[index];
    },
    selectedExperienceCategoryTitle(): string {
      const experienceCategory = this.experienceCategories.find(
        category => category.id === this.experienceCategoryId,
      );

      return experienceCategory?.title ?? '';
    },
    ecommerceEvent(): ECommerceData {
      const currentSearchVariables = this.currentSearchVariables;
      return {
        location_id: currentSearchVariables.destination?.ridcode,
        arrival_date: currentSearchVariables.dates?.dateIn,
        departure_date: currentSearchVariables.dates?.dateOut,
        room_nb: currentSearchVariables.rooms.length,
        night_nb: currentSearchVariables.dates?.nights,
        adults_nb: currentSearchVariables.rooms.reduce(
          (acc, obj) => acc + obj.adults,
          0,
        ),
        children_nb: currentSearchVariables.rooms.reduce(
          (acc, obj) => acc + obj.childrenAges.length,
          0,
        ),
      };
    },
  },
  state() {
    return {
      currency: null,
      dates: [],
      destination: {
        text: '',
      },
      experienceCategories: [],
      experienceCategoryId: 'all',
      fetchError: null,
      hotelId: null,
      includeFlights: false,
      loading: true,
      origin: {
        text: '',
      },
      rooms: [],
      searchHotelsResult: {
        alternativeHotels: [],
        hotels: [],
        variables: {
          rooms: [],
        },
      },
      validateRoomInputs: false,
    };
  },
});
